import { Reveal, Eyebrow } from "@/components/Reveal";
import { STORE, IMAGES } from "@/lib/catalog";
import { HeartHandshake, Leaf, Scale, Users } from "lucide-react";

const VALUES = [
  { icon: Leaf, title: "Freshness First", text: "Stock rotated daily. If it is not fresh enough for our own kitchen, it does not reach yours." },
  { icon: Scale, title: "Honest Weighing", text: "Calibrated scales, sealed packs and transparent MRP billing — no games, ever." },
  { icon: HeartHandshake, title: "Credit & Trust", text: "The old-school khata system lives on. Regulars can buy now and settle monthly." },
  { icon: Users, title: "Community Rooted", text: "We know our customers by name — and their usual orders by heart." },
];

export default function About() {
  return (
    <main className="bg-bone" data-testid="about-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <Reveal>
          <Eyebrow>About Us</Eyebrow>
          <h1 className="mt-4 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink max-w-3xl">
            A small shop with a
            <span className="italic text-forest"> big promise.</span>
          </h1>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-12 items-start">
          <Reveal className="space-y-5 text-ink-soft leading-relaxed">
            <p>
              Shivam Kirana Store opened its shutters in 2002 on Main Market Road, Kanpur,
              with a single wooden counter, a brass weighing scale, and one simple belief —
              <span className="font-semibold text-ink"> a neighbourhood runs on trust.</span>
            </p>
            <p>
              Two decades later, the counter is bigger and the shelves are fuller, but the
              belief has not changed. We still source our atta, dal and spices from producers
              we know personally. We still hand-check every batch. And we still deliver to
              your doorstep ourselves — usually within the hour.
            </p>
            <p>
              From the morning's first pouch of milk to the late-evening sugar refill before
              guests arrive, we are open every day of the week, because a kitchen never takes
              a holiday.
            </p>
            <div className="rounded-2xl bg-cream border border-line p-6">
              <p className="font-serif italic text-lg text-forest">
                "Jaisa apne ghar ke liye chunte hain, waisa hi aapke liye. We pick for you
                exactly what we would pick for our own family."
              </p>
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-marigold">
                {STORE.owner} · Owner, Shivam Kirana Store
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="grid grid-cols-2 gap-5">
            <div className="overflow-hidden rounded-t-[6rem] rounded-b-2xl shadow-xl col-span-2">
              <img src={IMAGES.spices} alt="Shelves of authentic Indian spices" className="h-64 w-full object-cover hover:scale-105 transition-transform duration-700" data-testid="about-spices-image" />
            </div>
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img src={IMAGES.market} alt="Fresh market produce" className="h-44 w-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img src={IMAGES.snacks} alt="Snacks and namkeen display" className="h-44 w-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-forest-dark text-bone py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow className="text-saffron">What We Stand For</Eyebrow>
            <h2 className="mt-4 font-serif font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              Values on every shelf.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="rounded-2xl bg-bone/5 border border-bone/10 p-6 h-full hover:bg-bone/10 transition-colors duration-300">
                  <v.icon className="w-7 h-7 text-saffron" strokeWidth={1.5} />
                  <h3 className="mt-4 font-serif font-semibold text-lg">{v.title}</h3>
                  <p className="mt-2 text-sm text-bone/65 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
